import { Badge, Button } from '@lensora/ui'
import { BarChart3, Ban } from 'lucide-react'
import * as React from 'react'

import { SCOPE_LABEL } from './scopes'

import type { McpApiKey } from '@/hooks/use-mcp-keys'
import { useRevokeMcpKey } from '@/hooks/use-mcp-keys'


interface KeyRowProps {
  mcpKey: McpApiKey
  onViewUsage: (key: McpApiKey) => void
}

function formatDate(value: string | null): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const PURPOSE_LABEL: Record<McpApiKey['purpose'], string> = {
  mcp: 'MCP',
  looker_studio: 'Looker Studio',
}

export function KeyRow({ mcpKey, onViewUsage }: KeyRowProps) {
  const [confirming, setConfirming] = React.useState(false)
  const revoke = useRevokeMcpKey()

  const isRevoked = !!mcpKey.revokedAt
  const isExpired = !!mcpKey.expiresAt && new Date(mcpKey.expiresAt) < new Date()
  const isInactive = isRevoked || isExpired

  return (
    <div className={`flex items-center justify-between gap-4 px-6 py-4 ${isInactive ? 'opacity-60' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-text">{mcpKey.name}</p>
          <Badge variant={mcpKey.purpose === 'looker_studio' ? 'secondary' : 'outline'} className="text-[10px]">
            {PURPOSE_LABEL[mcpKey.purpose]}
          </Badge>
          {isRevoked && <Badge variant="destructive">Revoked</Badge>}
          {!isRevoked && isExpired && <Badge variant="outline">Expired</Badge>}
        </div>
        <p className="mt-0.5 truncate text-xs text-text-muted">
          {mcpKey.keyPrefix}••••••••
        </p>
        {mcpKey.purpose === 'mcp' && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {mcpKey.scopes.map((s) => (
              <Badge key={s} variant="outline" className="text-[10px]">{SCOPE_LABEL[s] ?? s}</Badge>
            ))}
          </div>
        )}
      </div>

      <div className="hidden shrink-0 text-right text-xs text-text-muted sm:block">
        <p>
          {mcpKey.lastClientName
            ? `${mcpKey.lastClientName}${mcpKey.lastClientVersion ? ` v${mcpKey.lastClientVersion}` : ''}`
            : 'Never connected'}
        </p>
        <p className="mt-0.5">Expires {mcpKey.expiresAt ? formatDate(mcpKey.expiresAt) : 'never'}</p>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <Button variant="ghost" size="sm" onClick={() => onViewUsage(mcpKey)}>
          <BarChart3 className="h-3.5 w-3.5" />
        </Button>

        {!isRevoked && (
          confirming ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(mcpKey.id, { onSettled: () => setConfirming(false) })}
              >
                {revoke.isPending ? 'Revoking…' : 'Confirm'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)} title="Revoke key">
              <Ban className="h-3.5 w-3.5 text-feedback-error" />
            </Button>
          )
        )}
      </div>
    </div>
  )
}
